import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  Ban,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock,
  FileText,
  Home,
  LayoutGrid,
  Maximize2,
  Loader2,
  RefreshCw,
  Search,
  ShieldCheck,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useAuditTrailDialog } from '@/components/audit/auditTrailDialogContext'
import { useFacilityOverview, useSiteLayout } from '@/hooks/useFacility'
import type { LayoutBuilding, LayoutFloor, OverviewRoom, SiteLayout } from '@/api/facilityApi'
import { CampusMap } from './CampusMap'
import { ReturnToServiceDialog } from '../ReturnToServiceDialog'
import { sortFloors } from '@/lib/siteLayout'
import {
  STATUS_META,
  attentionCount,
  cleanExpiresAt,
  cleaningProgress,
  countStatuses,
  inSummary,
  matchesSearch,
  nextScheduled,
  roomFooter,
  roomStatus,
  sortRooms,
  summary,
  type NextClean,
  type RoomSort,
  type SummaryKey,
  type RoomStatus,
} from '@/lib/roomStatus'

/** `YYYY-MM-DD` in local time, `days` from `d`. */
function localIso(d: Date, days = 0): string {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate() + days)
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`
}

const STATUS_ICON: Record<RoomStatus, typeof Check> = {
  released: CheckCircle2,
  expiring: Clock,
  expired: AlertTriangle,
  cleaning: RefreshCw,
  awaiting: ShieldCheck,
  out_of_service: Ban,
}

/** Ticks every 30 s so hold-time countdowns stay current with the page open. */
function useNow() {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000)
    return () => window.clearInterval(id)
  }, [])
  return now
}

/**
 * Overall View (Cleaning Visual p.3, z8nrz7f731): the campus, then a building,
 * then one of its floors, its rooms by status, and one room in detail.
 */
export function OverallViewTab({
  onOpenTasks,
  onSetUp,
  onOpenLayout,
  focus,
}: {
  /** The Tasks tab, for a room with no run to open. */
  onOpenTasks: () => void
  onSetUp: () => void
  /** The campus level (Layout #2): where the breadcrumb's "Campus" goes. */
  onOpenLayout: () => void
  /** A building picked on the Layout: show it. `at` makes picking the same one again count. */
  focus?: { buildingId: string; at: number } | null
}) {
  const now = useNow()
  const audit = useAuditTrailDialog()
  const navigate = useNavigate()
  const { data: layout, isLoading: layoutLoading, error: layoutError } = useSiteLayout()
  const { data: overview, isLoading: overviewLoading, error: overviewError } = useFacilityOverview()
  const [buildingId, setBuildingId] = useState<string | null>(null)
  const [floorId, setFloorId] = useState<string | null>(null)
  const [roomId, setRoomId] = useState<string | null>(null)
  // "Out of service" opens the signed return to service (z8nrz7fzyk).
  const [returning, setReturning] = useState<{ id: string; label: string } | null>(null)
  const [sort, setSort] = useState<RoomSort>('urgency')
  const [q, setQ] = useState('')
  // A KPI card picked: only this floor's rooms of that status.
  const [statusFilter, setStatusFilter] = useState<RoomStatus | null>(null)

  const rooms = overview?.rooms ?? []
  const roomsById = useMemo(() => new Map(rooms.map((r) => [r.id, r])), [rooms])
  const buildings = layout?.buildings ?? []
  const building = buildings.find((b) => b.id === buildingId) ?? buildings[0]
  const floors = useMemo(() => sortFloors(building?.floors ?? []), [building])
  const floor = floors.find((f) => f.id === floorId) ?? floors[0]
  const floorRooms = useMemo(
    () => (floor ? floor.rooms.map((r) => roomsById.get(r.id)).filter((r): r is OverviewRoom => !!r) : []),
    [floor, roomsById],
  )
  const shown = useMemo(
    () =>
      sortRooms(
        floorRooms.filter((r) => matchesSearch(r, q) && (!statusFilter || roomStatus(r, now) === statusFilter)),
        sort,
        now,
      ),
    [floorRooms, q, statusFilter, sort, now],
  )
  const room = (roomId && roomsById.get(roomId)) || shown[0]

  // Drilling down and back up. Explicit rather than effects on the ids, so
  // jumping straight to a room (from a summary chip) sets all three at once.
  const selectBuilding = (id: string) => {
    setBuildingId(id)
    setFloorId(null)
    setRoomId(null)
    setStatusFilter(null)
  }
  const selectFloor = (id: string | null) => {
    setFloorId(id)
    setRoomId(null)
    setStatusFilter(null)
  }
  const jumpToRoom = (r: OverviewRoom) => {
    if (!r.buildingId || !r.floorId) return
    setBuildingId(r.buildingId)
    setFloorId(r.floorId)
    setRoomId(r.id)
    setStatusFilter(null)
    setQ('')
  }

  useEffect(() => {
    if (focus) selectBuilding(focus.buildingId)
  }, [focus])

  if (layoutLoading || overviewLoading) {
    return (
      <div className='flex items-center gap-2 rounded-xl border bg-white p-10 text-sm text-muted-foreground'>
        <Loader2 className='h-4 w-4 animate-spin' /> Loading the overall view…
      </div>
    )
  }
  // A failed load is said as such: an empty view here would read as "no
  // buildings" or "no rooms" and hide expired rooms.
  const loadError = layoutError ?? overviewError
  if (loadError || !overview) {
    return (
      <p role='alert' className='rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive'>
        Could not load the overall view{loadError instanceof Error ? `: ${loadError.message}` : '.'}
      </p>
    )
  }
  if (!layout || buildings.length === 0) {
    return (
      <div className='rounded-xl border border-dashed bg-white p-10 text-center text-sm text-muted-foreground'>
        No buildings set up yet.{' '}
        <button type='button' onClick={onSetUp} className='font-medium text-primary hover:underline'>
          Set up a building
        </button>{' '}
        and map its rooms to see them here.
      </div>
    )
  }

  const totals = summary(rooms, now)
  const badges = Object.fromEntries(
    buildings.map((b) => [b.id, attentionCount(rooms.filter((r) => r.buildingId === b.id), now)]),
  )

  return (
    <div className='space-y-3'>
      <SummaryBar totals={totals} rooms={rooms} now={now} layout={layout} onJump={jumpToRoom} />

      <div className='rounded-xl border bg-white p-3 shadow-sm'>
        <div className='mb-3 flex items-center gap-3'>
          <Breadcrumb
            building={building}
            floor={floor}
            room={room}
            onCampus={onOpenLayout}
            onBuilding={() => building && selectBuilding(building.id)}
            onFloor={() => floor && selectFloor(floor.id)}
          />
          <label className='relative ml-auto w-[280px]'>
            <Search className='pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400' />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder='Search room, grade, batch or operator'
              aria-label='Search rooms'
              className='h-9 w-full rounded-lg border border-gray-200 bg-white pl-8 pr-3 text-xs text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30'
            />
          </label>
        </div>

        <KpiRow rooms={floorRooms} now={now} selected={statusFilter} onSelect={(s) => setStatusFilter((cur) => (cur === s ? null : s))} />

        {/* The spec's proportions: campus a fifth, rooms over half, the room detail a quarter. */}
        <div className='mt-3 grid gap-3 lg:grid-cols-[minmax(200px,20fr)_minmax(0,55fr)_minmax(270px,25fr)]'>
          <section className='rounded-lg border border-gray-200 p-3'>
            <h3 className='mb-3 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[#1e2a45]'>
              <Home className='h-4 w-4 text-[#1a73e8]' /> Campus
              <button
                type='button'
                onClick={onOpenLayout}
                className='ml-auto rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700'
                aria-label='Open the full site layout'
                title='Open the full site layout'
              >
                <Maximize2 className='h-3.5 w-3.5' />
              </button>
            </h3>
            <CampusMap
              buildings={buildings}
              links={layout.links}
              selectedId={building?.id ?? null}
              onSelect={selectBuilding}
              badges={badges}
              fit
            />
          </section>

          <section className='min-w-0 rounded-lg border border-gray-200 p-3'>
            <FloorTabs floors={floors} selectedId={floor?.id ?? null} onSelect={selectFloor} roomsById={roomsById} now={now} />
            <div className='mt-3 border-t-2 border-[#3b3f45] pt-3'>
              <div className='flex flex-wrap items-start gap-3'>
                <div className='min-w-0'>
                  <h3 className='text-[15px] font-bold text-[#1e2a45]'>
                    Rooms · {building?.name}
                    {floor ? ` / ${floor.name}` : ''}
                    {floor?.description ? ` — ${floor.description}` : ''}
                  </h3>
                  <p className='text-[11px] text-gray-400'>
                    {statusFilter ? (
                      <>
                        Showing {shown.length} of {floorRooms.length} · {STATUS_META[statusFilter].label.toLowerCase()} only ·{' '}
                        <button type='button' onClick={() => setStatusFilter(null)} className='font-medium text-[#1a73e8] hover:underline'>
                          Show all
                        </button>
                      </>
                    ) : (
                      <>
                        {floorRooms.length} rooms · sorted by {sort === 'room' ? 'room no.' : sort}
                      </>
                    )}
                  </p>
                </div>
                <SortToggle value={sort} onChange={setSort} />
              </div>
              {shown.length === 0 ? (
                <p className='py-8 text-center text-sm text-muted-foreground'>
                  {!floor
                    ? 'This building has no floors yet.'
                    : statusFilter && floorRooms.length > 0
                      ? `No ${STATUS_META[statusFilter].label.toLowerCase()} rooms on this floor.`
                      : q
                        ? 'No room on this floor matches.'
                        : 'No rooms on this floor yet.'}
                </p>
              ) : (
                <div className='mt-3 grid gap-2.5 sm:grid-cols-2 xl:grid-cols-4'>
                  {shown.map((r) => (
                    <RoomCard
                      key={r.id}
                      room={r}
                      now={now}
                      selected={r.id === room?.id}
                      onSelect={() => setRoomId(r.id)}
                      onReturnToService={() => setReturning({ id: r.id, label: r.label })}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className='rounded-lg border border-gray-200 p-4'>
            {room ? (
              <RoomDetail
                room={room}
                now={now}
                next={nextScheduled(room)}
                onClose={() => setRoomId(null)}
                onPrimary={() => {
                  const run = room.active?.procedureId
                  if (run) navigate(`/cleaning/runs/${run}`)
                  else onOpenTasks()
                }}
                // The room's own trail, in the app's audit pop-up (z8nrz7dq6q).
                onAudit={() => audit?.open({ kind: 'equipment', id: room.id, label: room.label, openItem: null })}
                onReturnToService={() => setReturning({ id: room.id, label: room.label })}
              />
            ) : (
              <p className='py-10 text-center text-sm text-muted-foreground'>Pick a room.</p>
            )}
          </section>
        </div>
      </div>
      <ReturnToServiceDialog unit={returning} onClose={() => setReturning(null)} />
    </div>
  )
}

// ── Summary bar ──────────────────────────────────────────────────────────────

/** The campus-wide chips. Each opens the rooms it counts; picking one drills straight to it. */
function SummaryBar({
  totals,
  rooms,
  now,
  layout,
  onJump,
}: {
  totals: ReturnType<typeof summary>
  rooms: OverviewRoom[]
  now: Date
  layout: SiteLayout
  onJump: (room: OverviewRoom) => void
}) {
  const [open, setOpen] = useState<SummaryKey | null>(null)
  const where = useMemo(() => {
    const floorName = new Map<string, string>()
    for (const b of layout.buildings) for (const f of b.floors) floorName.set(f.id, `${b.name} / ${f.name}`)
    return (r: OverviewRoom) => (r.floorId ? floorName.get(r.floorId) ?? '—' : 'Not on a floor')
  }, [layout])
  const chips: { key: SummaryKey; label: string; Icon: typeof AlertTriangle; color: string; tint: string }[] = [
    { key: 'overdue', label: 'Overdue', Icon: AlertTriangle, color: '#d32626', tint: '#fdeaea' },
    { key: 'dueSoon', label: 'Due soon', Icon: Clock, color: '#b7791f', tint: '#fdf3dc' },
    { key: 'cleaning', label: 'Cleaning in progress', Icon: RefreshCw, color: '#2459d6', tint: '#e8effd' },
    { key: 'awaiting', label: 'Awaiting review', Icon: ShieldCheck, color: '#b0328d', tint: '#fbe9f5' },
    { key: 'clean', label: 'Clean', Icon: CheckCircle2, color: '#1f8a3b', tint: '#e9f6ed' },
    { key: 'outOfService', label: 'Out of service', Icon: Ban, color: '#4b5563', tint: '#eef0f3' },
  ]
  return (
    <div className='flex flex-wrap items-center gap-1.5'>
      <span className='mr-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400'>Campus</span>
      {chips.map(({ key, label, Icon, color, tint }) => {
        const matching = rooms.filter((r) => inSummary(key, r, now)).sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))
        return (
          <Popover key={key} open={open === key} onOpenChange={(o) => setOpen(o ? key : null)}>
            <PopoverTrigger asChild>
              <button
                type='button'
                className='inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[12px] font-medium transition-shadow hover:shadow-sm focus-visible:outline focus-visible:outline-2'
                style={{ color, background: tint, outlineColor: color, boxShadow: open === key ? `0 0 0 1.5px ${color}` : undefined }}
                aria-label={`${label}: ${totals[key]} rooms. Show them`}
              >
                <Icon className='h-3.5 w-3.5' strokeWidth={2} />
                {label}
                <span className='font-bold'>{totals[key]}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent align='start' className='w-72 p-2'>
              <p className='px-1.5 pb-1.5 text-[11px] font-semibold uppercase tracking-wide' style={{ color }}>
                {label} · {matching.length} {matching.length === 1 ? 'room' : 'rooms'}
              </p>
              {matching.length === 0 ? (
                <p className='px-1.5 py-2 text-xs text-muted-foreground'>None right now.</p>
              ) : (
                <ul className='max-h-64 overflow-y-auto' aria-label={`${label} rooms`}>
                  {matching.map((r) => (
                    <li key={r.id}>
                      <button
                        type='button'
                        disabled={!r.floorId}
                        onClick={() => {
                          onJump(r)
                          setOpen(null)
                        }}
                        className='flex w-full items-center gap-2 rounded px-1.5 py-1.5 text-left text-xs hover:bg-gray-50 disabled:cursor-default disabled:hover:bg-transparent'
                        title={r.floorId ? 'Show this room' : 'Map it to a floor to see it here'}
                      >
                        <span className='font-mono font-semibold text-gray-800'>{r.label}</span>
                        <span className='ml-auto truncate text-[11px] text-gray-500'>{where(r)}</span>
                        {r.floorId && <ChevronRight className='h-3 w-3 shrink-0 text-gray-300' />}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </PopoverContent>
          </Popover>
        )
      })}
    </div>
  )
}

/** Where you are, and the way back up: Campus (the Layout), the building, the floor. */
function Breadcrumb({
  building,
  floor,
  room,
  onCampus,
  onBuilding,
  onFloor,
}: {
  building?: LayoutBuilding
  floor?: LayoutFloor
  room?: OverviewRoom
  onCampus: () => void
  onBuilding: () => void
  onFloor: () => void
}) {
  const parts: { label: string; onClick?: () => void }[] = [
    { label: 'Campus', onClick: onCampus },
    ...(building ? [{ label: building.name, onClick: onBuilding }] : []),
    ...(floor ? [{ label: floor.name, onClick: onFloor }] : []),
    ...(room ? [{ label: `Room ${room.label}` }] : []),
  ]
  return (
    <nav aria-label='Location' className='flex items-center gap-1.5 text-[12px] text-gray-500'>
      <LayoutGrid className='h-4 w-4 text-[#1a73e8]' />
      {parts.map((p, i) => {
        const last = i === parts.length - 1
        return (
          <span key={i} className='flex items-center gap-1.5'>
            {i > 0 && <ChevronRight className='h-3 w-3 text-gray-300' />}
            {p.onClick && !last ? (
              <button type='button' onClick={p.onClick} className='rounded hover:text-[#1a73e8] hover:underline'>
                {p.label}
              </button>
            ) : (
              <span className={cn(last ? 'font-bold text-[#1e2a45]' : '')} aria-current={last ? 'location' : undefined}>
                {p.label}
              </span>
            )}
          </span>
        )
      })}
    </nav>
  )
}

// ── KPI row ──────────────────────────────────────────────────────────────────

/** The floor's six statuses. Picking one shows only its rooms; picking it again shows them all. */
function KpiRow({
  rooms,
  now,
  selected,
  onSelect,
}: {
  rooms: OverviewRoom[]
  now: Date
  selected: RoomStatus | null
  onSelect: (status: RoomStatus) => void
}) {
  const c = countStatuses(rooms, now)
  const operators = new Set(rooms.filter((r) => roomStatus(r, now) === 'cleaning' && r.operator).map((r) => r.operator)).size
  const released = rooms.length ? Math.round((c.released / rooms.length) * 100) : 0
  const oosNote = rooms.find((r) => r.outOfService && r.statusNote)?.statusNote
  const cards: { status: RoomStatus; title: string; sub: string }[] = [
    { status: 'expired', title: 'Expired', sub: 'Not available for processing' },
    { status: 'expiring', title: 'Expiring soon', sub: 'Within 4 hours' },
    { status: 'cleaning', title: 'Cleaning now', sub: `${operators} operator${operators === 1 ? '' : 's'}` },
    { status: 'awaiting', title: 'Awaiting review', sub: 'QA sign-off pending' },
    { status: 'released', title: 'Released for use', sub: `${released}% of floor` },
    { status: 'out_of_service', title: 'Out of service', sub: oosNote || 'Blocked' },
  ]
  return (
    <div className='grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6'>
      {cards.map(({ status, title, sub }) => {
        const meta = STATUS_META[status]
        const Icon = STATUS_ICON[status]
        return (
          <button
            key={status}
            type='button'
            onClick={() => onSelect(status)}
            aria-pressed={selected === status}
            // The spec's legend text, where the status is: what it means, and what a click does.
            title={`${meta.label}: ${meta.description} ${selected === status ? 'Click to show all rooms.' : 'Click to show only these rooms.'}`}
            className='flex items-center gap-3 overflow-hidden rounded-md border border-gray-200 bg-white py-2 pl-0 pr-3 text-left shadow-sm transition-shadow hover:shadow-md'
            style={selected === status ? { boxShadow: `0 0 0 2px ${meta.color}` } : undefined}
          >
            <span className='w-[5px] self-stretch' style={{ background: meta.color }} />
            <div className='flex min-w-[56px] flex-col'>
              <span className='flex items-center gap-1.5'>
                <Icon className='h-5 w-5' style={{ color: meta.color }} strokeWidth={2} />
                <span className='text-[22px] font-bold leading-none text-[#1e2a45]'>{c[status]}</span>
              </span>
              <span className='mt-1 text-[9px] font-bold uppercase tracking-wide' style={{ color: meta.color }}>
                {meta.caption}
              </span>
            </div>
            <div className='min-w-0'>
              <p className='truncate text-[12px] font-semibold text-[#1e2a45]'>{title}</p>
              <p className='truncate text-[10px] text-gray-400'>{sub}</p>
            </div>
          </button>
        )
      })}
    </div>
  )
}

// ── Floors ───────────────────────────────────────────────────────────────────

function StatusStrip({ rooms, now, selected }: { rooms: OverviewRoom[]; now: Date; selected: boolean }) {
  const c = countStatuses(rooms, now)
  const order: RoomStatus[] = ['expired', 'expiring', 'cleaning', 'awaiting', 'released', 'out_of_service']
  return (
    <div className={cn('flex h-[7px] w-full overflow-hidden rounded-[2px]', selected ? 'ring-1 ring-[#2251d6]/30' : '')}>
      {rooms.length === 0 ? (
        <span className='flex-1 bg-gray-200' />
      ) : (
        order.map((s) => (c[s] ? <span key={s} style={{ flexGrow: c[s], background: STATUS_META[s].color }} /> : null))
      )}
    </div>
  )
}

function FloorTabs({
  floors,
  selectedId,
  onSelect,
  roomsById,
  now,
}: {
  floors: LayoutFloor[]
  selectedId: string | null
  onSelect: (id: string) => void
  roomsById: Map<string, OverviewRoom>
  now: Date
}) {
  if (floors.length === 0) return <p className='text-xs text-muted-foreground'>No floors in this building yet.</p>
  return (
    // Fixed-width tabs: stretched across the panel, a single floor became one
    // solid block of colour.
    <div role='tablist' aria-label='Floors' className='flex flex-wrap gap-2'>
      {floors.map((f) => {
        const selected = f.id === selectedId
        const rooms = f.rooms.map((r) => roomsById.get(r.id)).filter((r): r is OverviewRoom => !!r)
        return (
          <button
            key={f.id}
            role='tab'
            aria-selected={selected}
            type='button'
            onClick={() => onSelect(f.id)}
            className={cn(
              'w-[200px] max-w-full rounded-lg border px-3 py-2 text-left transition-colors',
              selected
                ? 'border-[#2251d6] bg-[#eef3fe] shadow-[inset_3px_0_0_#2251d6]'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50',
            )}
          >
            <span className={cn('block text-[12px] font-bold', selected ? 'text-[#1a3fae]' : 'text-[#1e2a45]')}>{f.name}</span>
            <span className='block truncate text-[10px] text-gray-500'>{f.description || ' '}</span>
            <span className='mt-1.5 block'>
              <StatusStrip rooms={rooms} now={now} selected={selected} />
            </span>
            <span className='mt-1 block text-[10px] text-gray-500'>{f.rooms.length === 1 ? '1 room' : `${f.rooms.length} rooms`}</span>
          </button>
        )
      })}
    </div>
  )
}

function SortToggle({ value, onChange }: { value: RoomSort; onChange: (v: RoomSort) => void }) {
  const opts: { v: RoomSort; label: string }[] = [
    { v: 'urgency', label: 'Urgency' },
    { v: 'grade', label: 'Grade' },
    { v: 'room', label: 'Room no.' },
  ]
  return (
    <div role='radiogroup' aria-label='Sort rooms' className='ml-auto flex rounded-lg border border-gray-200 bg-gray-50 p-0.5'>
      {opts.map((o) => (
        <button
          key={o.v}
          type='button'
          role='radio'
          aria-checked={value === o.v}
          onClick={() => onChange(o.v)}
          className={cn(
            'rounded-md px-5 py-1 text-[11px] transition-colors',
            value === o.v ? 'border border-gray-200 bg-white font-semibold text-[#1e2a45] shadow-sm' : 'text-gray-500 hover:text-gray-700',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

// ── Room card ────────────────────────────────────────────────────────────────

/** The small readiness dot: red once expired, amber while cleaning, otherwise green. */
const dotColor = (s: RoomStatus) => (s === 'expired' ? '#c62828' : s === 'cleaning' ? '#c2610c' : '#1f7a36')

/** A room's status. "Out of service", given `onClick`, is a link to put it back in service. */
function StatusPill({ status, onClick }: { status: RoomStatus; onClick?: () => void }) {
  const meta = STATUS_META[status]
  const Icon = STATUS_ICON[status]
  const className = 'inline-flex w-full max-w-[150px] items-center gap-1.5 rounded-full border px-2.5 py-[3px] text-[11px] font-semibold'
  const style = { color: meta.color, background: meta.tint, borderColor: `${meta.color}55` }
  if (status === 'out_of_service' && onClick) {
    return (
      <button
        type='button'
        onClick={onClick}
        title='Return to service'
        className={cn(className, 'underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2251d6]')}
        style={style}
      >
        <Icon className='h-3 w-3' strokeWidth={2.25} />
        {meta.label}
      </button>
    )
  }
  return (
    <span className={className} style={style}>
      <Icon className='h-3 w-3' strokeWidth={2.25} />
      {meta.label}
    </span>
  )
}

function RoomCard({
  room,
  now,
  selected,
  onSelect,
  onReturnToService,
}: {
  room: OverviewRoom
  now: Date
  selected: boolean
  onSelect: () => void
  onReturnToService: () => void
}) {
  const status = roomStatus(room, now)
  const meta = STATUS_META[status]
  const urgent = status === 'expired' || status === 'expiring'
  // The card selects the room through a button laid over it, so the
  // "Out of service" link inside is a button of its own, not one nested in it.
  return (
    <div
      className={cn(
        'relative flex overflow-hidden rounded-lg bg-white text-left shadow-sm transition-shadow hover:shadow',
        selected ? 'border-2 border-[#2251d6]' : 'border border-gray-200',
      )}
    >
      <button
        type='button'
        onClick={onSelect}
        aria-pressed={selected}
        aria-label={`${room.label} ${room.name} · ${meta.label}`}
        className='absolute inset-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2251d6]'
      />
      <span className='pointer-events-none my-1.5 ml-1.5 w-[4px] shrink-0 rounded-sm' style={{ background: meta.color }} />
      <span className='pointer-events-none min-w-0 flex-1 px-2.5 py-2'>
        <span className='flex items-start justify-between gap-2'>
          <span className='text-[15px] font-bold leading-5 text-[#1e2a45]'>{room.label}</span>
          {room.grade && (
            <span className='rounded-full border border-gray-300 px-2.5 py-[1px] text-[9px] font-medium text-gray-500'>Gr {room.grade}</span>
          )}
        </span>
        <span className='block truncate text-[10px] text-gray-500'>{room.name}</span>
        <span className='pointer-events-auto relative mt-1.5 block w-fit'>
          <StatusPill status={status} onClick={onReturnToService} />
        </span>
        <span className='mt-1.5 flex items-center gap-1.5'>
          <Clock className='h-3 w-3 shrink-0 text-gray-400' />
          <span className={cn('flex-1 truncate text-[10px]', urgent ? 'font-semibold' : 'text-gray-600')} style={urgent ? { color: meta.color } : undefined}>
            {roomFooter(room, now)}
          </span>
          <span className='h-[11px] w-[11px] shrink-0 rounded-full' style={{ background: dotColor(status) }} />
        </span>
      </span>
    </div>
  )
}

// ── Room detail ──────────────────────────────────────────────────────────────

const dayAndTime = (d: Date, now: Date) => {
  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString()
  const tomorrow = new Date(now.getTime() + 86_400_000)
  const yesterday = new Date(now.getTime() - 86_400_000)
  const t = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  if (sameDay(d, now)) return `Today ${t}`
  if (sameDay(d, tomorrow)) return `Tomorrow ${t}`
  if (sameDay(d, yesterday)) return `Yesterday ${t}`
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${t}`
}

function RoomDetail({
  room,
  now,
  next,
  onClose,
  onPrimary,
  onAudit,
  onReturnToService,
}: {
  room: OverviewRoom
  now: Date
  next: NextClean | null
  onClose: () => void
  onPrimary: () => void
  onAudit: () => void
  /** "Out of service" is a link to the signed return to service (z8nrz7fzyk). */
  onReturnToService: () => void
}) {
  const status = roomStatus(room, now)
  const meta = STATUS_META[status]
  const Icon = STATUS_ICON[status]
  const expires = cleanExpiresAt(room)
  const progress = cleaningProgress(room)
  const sub = [room.name, room.grade ? `Grade ${room.grade}` : null, room.areaSqm ? `${room.areaSqm} m²` : null].filter(Boolean).join(' · ')
  const headline: Record<RoomStatus, string> = {
    released: 'Released for use',
    expiring: 'Clean expiring soon',
    expired: 'Clean expired',
    cleaning: 'Cleaning in progress',
    awaiting: 'Awaiting review',
    out_of_service: 'Out of service',
  }
  const detailLine =
    status === 'cleaning' && room.active
      ? room.active.stepsTotal
        ? `Step ${Math.min(room.active.stepsDone + 1, room.active.stepsTotal)} of ${room.active.stepsTotal}${room.active.currentStep ? ` — ${room.active.currentStep.toLowerCase()}` : ''}`
        : 'No procedure steps linked'
      : roomFooter(room, now)
  const rows: [string, string][] = [
    ['Clean hold time', room.holdTimeHours ? `${room.holdTimeHours} h${room.grade ? ` (Grade ${room.grade})` : ''}` : 'Not set'],
    ['Clean expires', expires ? dayAndTime(expires, now) : '—'],
    ['Last released', room.lastCleanEndedAt ? dayAndTime(new Date(room.lastCleanEndedAt), now) : '—'],
    ['SOP', room.sop ?? '—'],
    ['Agent', '—'],
    ['Operator', room.operator ?? '—'],
    ['QA verifier', status === 'awaiting' ? 'Pending' : '—'],
  ]
  const primaryLabel = room.active?.procedureId ? 'Open the cleaning run' : 'Open cleaning tasks'
  return (
    <div className='flex h-full flex-col'>
      <div className='flex items-start justify-between'>
        <div>
          <h3 className='text-[17px] font-bold text-[#1e2a45]'>Room {room.label}</h3>
          <p className='text-[10px] text-gray-500'>{sub}</p>
        </div>
        <button type='button' onClick={onClose} aria-label='Close room detail' className='rounded p-0.5 text-gray-400 hover:text-gray-600'>
          <X className='h-4 w-4' />
        </button>
      </div>

      <div className='mt-3 rounded-lg border px-3 py-2.5' style={{ borderColor: `${meta.color}66`, background: meta.tint }}>
        <div className='flex items-center gap-2.5'>
          <Icon className='h-6 w-6 shrink-0' style={{ color: meta.color }} strokeWidth={2} />
          <div className='min-w-0'>
            {status === 'out_of_service' ? (
              <button
                type='button'
                onClick={onReturnToService}
                title='Return to service'
                className='text-[13px] font-semibold underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2251d6]'
                style={{ color: meta.color }}
              >
                {headline[status]}
              </button>
            ) : (
              <p className='text-[13px] font-semibold' style={{ color: meta.color }}>
                {headline[status]}
              </p>
            )}
            <p className='truncate text-[10px]' style={{ color: meta.color }}>
              {detailLine}
            </p>
          </div>
        </div>
        {progress !== null && (
          <>
            <div className='mt-2 h-[9px] w-full border border-[#3b3f45] bg-white'>
              <div className='h-full' style={{ width: `${progress}%`, background: meta.color }} />
            </div>
            <p className='mt-0.5 text-[9px]' style={{ color: meta.color }}>
              {progress}%
            </p>
          </>
        )}
      </div>

      <dl className='mt-3 grid grid-cols-[110px_minmax(0,1fr)] gap-y-2 text-[11px]'>
        {rows.map(([k, v]) => (
          <div key={k} className='contents'>
            <dt className='text-gray-400'>{k}</dt>
            <dd className={cn('truncate font-semibold', v === '—' || v === 'Not set' ? 'font-normal text-gray-400' : 'text-[#1e2a45]')}>{v}</dd>
          </div>
        ))}
      </dl>

      <div className='mt-3 border-t-2 border-[#3b3f45] pt-2.5'>
        <p className='text-[9px] font-bold uppercase tracking-[0.14em] text-gray-400'>Environmental monitoring</p>
        <p className='mt-1.5 text-[11px] text-gray-400'>No results recorded.</p>
      </div>

      <div className='mt-2.5 border-t-2 border-[#3b3f45] pt-2.5 text-[11px]'>
        <div className='flex justify-between'>
          <span className='text-gray-400'>Next scheduled clean</span>
          <span className='text-right font-semibold text-[#1e2a45]'>
            {next ? dayLabel(next.date, now) : '—'}
            {next?.label && <span className='font-normal text-gray-500'> · {next.label}</span>}
          </span>
        </div>
        {next?.fromCycle && (
          <p className='mt-0.5 text-right text-[10px] text-gray-400'>From the room type's cycle · task not generated yet</p>
        )}
        {(status === 'expired' || status === 'out_of_service') && (
          <p className='mt-1 text-[10px] italic' style={{ color: STATUS_META.expiring.color }}>
            {status === 'expired' ? 'Line clearance required before entry' : 'Blocked — do not schedule for a batch'}
          </p>
        )}
      </div>

      <div className='mt-auto space-y-2 pt-4'>
        <button
          type='button'
          onClick={onPrimary}
          aria-label={primaryLabel}
          title={primaryLabel}
          className='flex h-10 w-full items-center justify-center rounded-[10px] bg-[#2251d6] text-white shadow hover:bg-[#1c45b9]'
        >
          <Check className='h-5 w-5' strokeWidth={2.5} />
        </button>
        <div className='grid grid-cols-2 gap-2'>
          <button
            type='button'
            onClick={onAudit}
            className='flex h-9 items-center justify-center gap-1.5 rounded-[10px] border border-gray-200 bg-white text-[12px] font-semibold text-[#2251d6] hover:bg-gray-50'
          >
            <FileText className='h-4 w-4' /> Audit trail
          </button>
          <button
            type='button'
            disabled
            title='Raising a deviation from a room is not set up yet.'
            className='flex h-9 items-center justify-center gap-1.5 rounded-[10px] border border-gray-200 bg-white text-[12px] font-semibold text-[#d32626] opacity-60'
          >
            <AlertTriangle className='h-4 w-4' /> Raise deviation
          </button>
        </div>
      </div>
    </div>
  )
}

/** "Today", "Tomorrow", else "Tue 13 Oct" for a `YYYY-MM-DD` date. */
function dayLabel(iso: string, now: Date): string {
  if (iso === localIso(now)) return 'Today'
  if (iso === localIso(now, 1)) return 'Tomorrow'
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
}
